
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Bell, Eye, Trash2, User, Phone, Mail, Calendar, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
  rental_request_id?: string;
  property_id?: string;
}

interface RentalRequest {
  id: string;
  message: string;
  status: string;
  created_at: string;
  tenant_name: string;
  tenant_phone: string;
  tenant_email: string;
  property_id: string;
  property: {
    title: string;
    address: string;
  };
}

const LandlordNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [rentalRequests, setRentalRequests] = useState<RentalRequest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
    fetchRentalRequests();
  }, []);

  const fetchNotifications = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      console.log('جلب الإشعارات للمالك:', user.id);

      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('خطأ في جلب الإشعارات:', error);
        return;
      }
      
      console.log('تم جلب الإشعارات:', data);
      setNotifications(data || []);
    } catch (error) {
      console.error('خطأ في جلب الإشعارات:', error);
    }
  };

  const fetchRentalRequests = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      console.log('جلب طلبات الإيجار للمالك:', user.id);

      const { data, error } = await supabase
        .from('rental_requests')
        .select(`
          *,
          property:properties!inner(title, address, owner_id)
        `)
        .eq('properties.owner_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('خطأ في جلب طلبات الإيجار:', error);
        return;
      }
      
      console.log('تم جلب طلبات الإيجار للمالك:', data);
      setRentalRequests(data || []);
    } catch (error) {
      console.error('خطأ في جلب طلبات الإيجار:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', id);

      if (error) throw error;
      
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === id ? { ...notif, is_read: true } : notif
        )
      );
    } catch (error) {
      console.error('خطأ في تعليم الإشعار كمقروء:', error);
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setNotifications(prev => prev.filter(notif => notif.id !== id));
      toast({
        title: "تم الحذف",
        description: "تم حذف الإشعار بنجاح",
      });
    } catch (error) {
      console.error('خطأ في حذف الإشعار:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء حذف الإشعار",
        variant: "destructive",
      });
    }
  };

  const deleteRentalRequest = async (requestId: string) => {
    if (!confirm('هل أنت متأكد من حذف هذا الطلب؟')) return;

    try {
      console.log('حذف طلب الإيجار:', requestId);

      const { error } = await supabase
        .from('rental_requests')
        .delete()
        .eq('id', requestId);

      if (error) throw error;

      setRentalRequests(prev => prev.filter(req => req.id !== requestId));

      toast({
        title: "تم الحذف",
        description: "تم حذف طلب الإيجار بنجاح",
      });

    } catch (error) {
      console.error('خطأ في حذف طلب الإيجار:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء حذف طلب الإيجار",
        variant: "destructive",
      });
    }
  };

  const updateRequestStatus = async (requestId: string, status: string) => {
    try {
      console.log('تحديث حالة الطلب:', requestId, 'إلى:', status);

      const { error } = await supabase
        .from('rental_requests')
        .update({ status })
        .eq('id', requestId);

      if (error) throw error;

      setRentalRequests(prev =>
        prev.map(req =>
          req.id === requestId ? { ...req, status } : req
        )
      );

      toast({
        title: "تم التحديث",
        description: `تم ${status === 'approved' ? 'قبول' : 'رفض'} الطلب`,
      });

      // إذا تم قبول الطلب، تحديث حالة العقار إلى مؤجر
      if (status === 'approved') {
        const request = rentalRequests.find(req => req.id === requestId);
        if (request) {
          const { error: propertyError } = await supabase
            .from('properties')
            .update({ occupied: true })
            .eq('id', request.property_id);

          if (propertyError) {
            console.error('خطأ في تحديث حالة العقار:', propertyError);
          }
        }
      }

    } catch (error) {
      console.error('خطأ في تحديث حالة الطلب:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء تحديث حالة الطلب",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* الإشعارات */}
      <Card>
        <CardHeader>
          <CardTitle className="text-right flex items-center gap-2">
            <Bell className="h-5 w-5 text-blue-600" />
            الإشعارات ({notifications.filter(n => !n.is_read).length} غير مقروء)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {notifications.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Bell className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>لا توجد إشعارات</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notifications.map(notification => (
                <div
                  key={notification.id}
                  className={`p-4 border rounded-lg ${
                    notification.is_read ? 'bg-gray-50' : 'bg-blue-50 border-blue-200'
                  }`}
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1 text-right">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-medium">{notification.title}</h4>
                        {!notification.is_read && (
                          <Badge variant="default" className="text-xs">جديد</Badge>
                        )}
                      </div>
                      <p className="text-gray-600 text-sm mb-2">{notification.message}</p>
                      <p className="text-xs text-gray-400">
                        {new Intl.DateTimeFormat('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }).format(new Date(notification.created_at))}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {!notification.is_read && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => markAsRead(notification.id)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteNotification(notification.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* طلبات الإيجار */}
      <Card>
        <CardHeader>
          <CardTitle className="text-right flex items-center gap-2">
            <User className="h-5 w-5 text-green-600" />
            طلبات الإيجار ({rentalRequests.filter(r => r.status === 'pending').length} في الانتظار)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {rentalRequests.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <User className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>لا توجد طلبات إيجار</p>
            </div>
          ) : (
            <div className="space-y-4">
              {rentalRequests.map(request => (
                <Card key={request.id} className="border border-gray-200">
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row justify-between gap-4">
                      <div className="flex-1 text-right">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-bold text-lg">{request.property?.title}</h4>
                          <Badge 
                            variant={
                              request.status === 'pending' ? 'default' :
                              request.status === 'approved' ? 'secondary' : 'destructive'
                            }
                          >
                            {request.status === 'pending' ? 'في الانتظار' :
                             request.status === 'approved' ? 'مقبول' : 'مرفوض'}
                          </Badge>
                        </div>
                        <p className="text-gray-600 mb-2">{request.property?.address}</p>
                        
                        <div className="bg-gray-50 p-3 rounded-lg mb-3">
                          <h5 className="font-medium mb-2">معلومات المتقدم:</h5>
                          <div className="space-y-1 text-sm">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4" />
                              <span>{request.tenant_name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4" />
                              <span>{request.tenant_email}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4" />
                              <span>{request.tenant_phone}</span>
                            </div>
                          </div>
                        </div>

                        {request.message && (
                          <div className="bg-blue-50 p-3 rounded-lg mb-3">
                            <h5 className="font-medium mb-2">رسالة المتقدم:</h5>
                            <p className="text-sm">{request.message}</p>
                          </div>
                        )}

                        <p className="text-xs text-gray-400 flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Intl.DateTimeFormat('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }).format(new Date(request.created_at))}
                        </p>
                      </div>

                      <div className="flex flex-col gap-2">
                        {request.status === 'pending' && (
                          <>
                            <Button
                              onClick={() => updateRequestStatus(request.id, 'approved')}
                              className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
                            >
                              <CheckCircle className="h-4 w-4" />
                              قبول الطلب
                            </Button>
                            <Button
                              variant="destructive"
                              onClick={() => updateRequestStatus(request.id, 'rejected')}
                              className="flex items-center gap-2"
                            >
                              <XCircle className="h-4 w-4" />
                              رفض الطلب
                            </Button>
                          </>
                        )}
                        <Button
                          variant="outline"
                          onClick={() => deleteRentalRequest(request.id)}
                          className="flex items-center gap-2 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                          حذف الطلب
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LandlordNotifications;
